import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscriptionService } from "../services/subscription";
import { authService } from "../services/auth";
import { toast } from "./use-toast";
import { limpiarCodigo } from "../lib/closerCode";

/**
 * Lógica de pago/suscripción de Stripe.
 *
 * Se extrajo en su día de `SubscriptionRequired.jsx` para poder usarla también
 * desde el registro, que entonces cobraba en la misma pantalla. Desde el
 * 13-ago-2026 hay **un solo checkout** y este hook vuelve a tener un solo
 * llamador — se conserva aparte porque ahí vive toda la conversación con
 * Stripe, incluida la confirmación con 3DS, y esa parte no se toca sin motivo.
 */
export function useSubscriptionPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [price, setPrice] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  // Planes disponibles (anual / mensual) y el elegido en el checkout.
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const loadPlans = async () => {
    try {
      const response = await subscriptionService.getPlans();
      if (response.success && response.data) {
        setPlans(response.data.plans || []);
        setSelectedPlan((current) => current || response.data.default_plan);
        return response.data;
      }
    } catch (error) {
      console.error("Error cargando planes:", error);
    }
    return null;
  };

  const loadPrice = async (plan) => {
    try {
      const response = await subscriptionService.getPrice(plan || selectedPlan);
      if (response.success && response.data) {
        setPrice(response.data);
      }
    } catch (error) {
      console.error("Error cargando precio:", error);
    }
  };

  const refreshUserAndSubscription = async () => {
    try {
      console.log("[SUBSCRIPTION] Refrescando datos del usuario...");
      const updatedUser = await authService.getCurrentUserFromBackend();

      if (updatedUser) {
        console.log("[SUBSCRIPTION] Usuario actualizado:", {
          id: updatedUser.id,
          name: updatedUser.name,
          subscription_status: updatedUser.subscription_status,
        });
      }

      console.log("[SUBSCRIPTION] Verificando estado de suscripción...");
      let attempts = 0;
      const maxAttempts = 5;
      let subscriptionActive = false;

      while (attempts < maxAttempts && !subscriptionActive) {
        attempts++;
        console.log(
          `[SUBSCRIPTION] Intento ${attempts}/${maxAttempts} de verificación...`,
        );

        try {
          const statusResponse = await subscriptionService.getStatus();

          const canAccessPremium =
            statusResponse?.data?.can_access_premium ??
            statusResponse?.data?.has_active_subscription ??
            statusResponse?.data?.is_active ??
            false;

          if (canAccessPremium) {
            subscriptionActive = true;
            console.log("[SUBSCRIPTION] ✅ Suscripción activa confirmada");
            break;
          } else {
            console.log(
              `[SUBSCRIPTION] ⏳ Suscripción aún no activa, esperando... (intento ${attempts}/${maxAttempts})`,
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.warn(
            `[SUBSCRIPTION] Error en intento ${attempts}:`,
            error.message,
          );
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      return subscriptionActive;
    } catch (error) {
      console.error(
        "[SUBSCRIPTION] Error refrescando usuario y suscripción:",
        error,
      );
      return false;
    }
  };

  const handlePayment = async (paymentMethod) => {
    setLoading(true);
    setError(null);

    try {
      // Paso 1: Obtener precio del plan elegido. El backend vuelve a resolver
      // el precio a partir del plan, así que price_id va solo como referencia.
      let priceId;
      if (price && price.price_id && price.plan === selectedPlan) {
        priceId = price.price_id;
      } else {
        const priceResponse = await subscriptionService.getPrice(selectedPlan);
        if (priceResponse && priceResponse.success) {
          priceId = priceResponse.data.price_id;
        } else {
          throw new Error("Error al obtener el precio de suscripción");
        }
      }

      // Paso 2: Verificar PaymentMethod
      if (
        !paymentMethod ||
        !paymentMethod.id ||
        !paymentMethod.id.startsWith("pm_")
      ) {
        throw new Error("El método de pago no es válido. Intenta nuevamente.");
      }

      console.log("💳 Creando suscripción...");

      // Pequeño delay para asegurar que Stripe haya procesado el PaymentMethod
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Paso 3: Crear suscripción
      console.log("[DEBUG] ========================================");
      console.log("[DEBUG] Llamando a POST /api/subscription/create");
      console.log("[DEBUG] payment_method_id:", paymentMethod.id);
      console.log("[DEBUG] price_id:", priceId);
      console.log("[DEBUG] ========================================");

      const subscriptionResponse = await subscriptionService.createSubscription(
        {
          payment_method_id: paymentMethod.id,
          price_id: priceId,
          ...(selectedPlan && { plan: selectedPlan }),
          ...(couponCode.trim() && { coupon: couponCode.trim() }),
        },
      );

      console.log("[DEBUG] ========================================");
      console.log("[DEBUG] Respuesta completa de /api/subscription/create");
      console.log("[DEBUG] success:", subscriptionResponse?.success);
      console.log("[DEBUG] error:", subscriptionResponse?.error);
      console.log("[DEBUG] message:", subscriptionResponse?.message);
      console.log("[DEBUG] ========================================");

      if (!subscriptionResponse || !subscriptionResponse.success) {
        console.error(
          "[DEBUG] ❌ ERROR: La respuesta del backend indica fallo",
        );
        console.error("[DEBUG] Error completo:", subscriptionResponse);
        throw new Error(
          subscriptionResponse?.error || "Error al crear suscripción",
        );
      }

      const subscriptionData = subscriptionResponse.data;

      console.log("[SUBSCRIPTION] ========================================");
      console.log(
        "[SUBSCRIPTION] Respuesta /subscription/create:",
        subscriptionData,
      );
      console.log(
        "[SUBSCRIPTION] stripe_subscription_id:",
        subscriptionData.stripe_subscription_id ||
          subscriptionData.subscription_id ||
          "NO ENCONTRADO",
      );
      console.log(
        "[SUBSCRIPTION] subscription_status:",
        subscriptionData.subscription_status ||
          subscriptionData.status ||
          "NO ENCONTRADO",
      );
      console.log(
        "[SUBSCRIPTION] client_secret:",
        subscriptionData.client_secret
          ? `Sí (${subscriptionData.client_secret.substring(0, 30)}...)`
          : "NO ENCONTRADO",
      );
      console.log(
        "[SUBSCRIPTION] client_secret_source:",
        subscriptionData.client_secret_source || "NO ENCONTRADO",
      );
      console.log(
        "[SUBSCRIPTION] requires_payment_confirmation:",
        subscriptionData.requires_payment_confirmation ?? false,
      );
      console.log(
        "[SUBSCRIPTION] payment_intent_status:",
        subscriptionData.payment_intent_status || "NO ENCONTRADO",
      );
      console.log("[SUBSCRIPTION] ========================================");

      const {
        stripe_subscription_id,
        subscription_status,
        client_secret,
        requires_payment_confirmation,
        payment_intent_status,
      } = subscriptionData;

      let paymentConfirmed = false;

      console.log("[SUBSCRIPTION] Evaluando si se debe confirmar el pago:", {
        has_client_secret: !!client_secret,
        requires_payment_confirmation,
        payment_intent_status,
      });

      if (requires_payment_confirmation === true && client_secret) {
        console.log("[SUBSCRIPTION] ========================================");
        console.log("[SUBSCRIPTION] confirmCardPayment llamado");
        console.log(
          "[SUBSCRIPTION] client_secret:",
          client_secret.substring(0, 30) + "...",
        );
        console.log("[SUBSCRIPTION] payment_method_id:", paymentMethod.id);
        console.log("[SUBSCRIPTION] ========================================");

        const { loadStripe } = await import("@stripe/stripe-js");
        const { STRIPE_PUBLIC_KEY } = await import("../config");
        const stripeInstance = await loadStripe(STRIPE_PUBLIC_KEY);

        if (!stripeInstance) {
          console.error("[SUBSCRIPTION] ❌ ERROR: No se pudo cargar Stripe");
          throw new Error(
            "No se pudo cargar Stripe. Por favor, recarga la página.",
          );
        }

        try {
          console.log(
            "[SUBSCRIPTION] Ejecutando stripe.confirmCardPayment()...",
          );
          const { error: confirmError, paymentIntent } =
            await stripeInstance.confirmCardPayment(client_secret, {
              payment_method: paymentMethod.id,
            });

          console.log(
            "[SUBSCRIPTION] ========================================",
          );
          console.log("[SUBSCRIPTION] Resultado confirmCardPayment");
          console.log(
            "[SUBSCRIPTION] confirmError:",
            confirmError
              ? {
                  type: confirmError.type,
                  code: confirmError.code,
                  message: confirmError.message,
                  decline_code: confirmError.decline_code,
                }
              : "null",
          );
          console.log(
            "[SUBSCRIPTION] paymentIntent:",
            paymentIntent
              ? {
                  id: paymentIntent.id,
                  status: paymentIntent.status,
                  client_secret: paymentIntent.client_secret
                    ? paymentIntent.client_secret.substring(0, 20) + "..."
                    : null,
                }
              : "null",
          );
          console.log(
            "[SUBSCRIPTION] ========================================",
          );

          if (confirmError) {
            console.error(
              "[SUBSCRIPTION] ❌ ERROR confirmando pago:",
              confirmError,
            );
            console.error(
              "[SUBSCRIPTION] Error completo:",
              JSON.stringify(confirmError, null, 2),
            );

            const errorMessage =
              confirmError.message ||
              "Error al confirmar el pago. Por favor, intenta nuevamente.";
            toast({
              title: "Error al procesar el pago",
              description: errorMessage,
              variant: "destructive",
            });

            throw new Error(errorMessage);
          }

          if (!paymentIntent) {
            console.error(
              "[SUBSCRIPTION] ❌ ERROR: No se recibió paymentIntent después de confirmar",
            );
            throw new Error(
              "No se recibió información del PaymentIntent después de confirmar.",
            );
          }

          console.log(
            "[SUBSCRIPTION] paymentIntent.status:",
            paymentIntent.status,
          );

          if (paymentIntent.status === "succeeded") {
            paymentConfirmed = true;
            console.log(
              "[SUBSCRIPTION] ✅ PaymentIntent succeeded - Pago confirmado exitosamente",
            );

            toast({
              title: "¡Pago confirmado!",
              description:
                "Tu pago se ha procesado correctamente. Actualizando suscripción...",
            });

            console.log(
              "[SUBSCRIPTION] Esperando que Stripe procese la suscripción (2 segundos)...",
            );
            await new Promise((resolve) => setTimeout(resolve, 2000));

            try {
              console.log(
                "[SUBSCRIPTION] Llamando a /api/subscription/refresh...",
              );
              const refreshResponse =
                await subscriptionService.refreshSubscription();

              console.log(
                "[SUBSCRIPTION] ========================================",
              );
              console.log(
                "[SUBSCRIPTION] Respuesta /subscription/refresh:",
                refreshResponse,
              );
              if (
                refreshResponse &&
                refreshResponse.success &&
                refreshResponse.data
              ) {
                const status = refreshResponse.data.status;
                console.log(
                  "[SUBSCRIPTION] subscription_status después de refresh:",
                  status,
                );
                console.log(
                  "[SUBSCRIPTION] payment_intent_status después de refresh:",
                  refreshResponse.data.payment_intent_status,
                );
                console.log(
                  "[SUBSCRIPTION] ========================================",
                );

                if (status === "active" || status === "trialing") {
                  console.log(
                    "[SUBSCRIPTION] ✅ Suscripción activa confirmada",
                  );
                  paymentConfirmed = true;
                } else {
                  console.warn(
                    "[SUBSCRIPTION] ⚠️ Suscripción aún no está activa:",
                    status,
                  );
                  console.log(
                    "[SUBSCRIPTION] Esperando 2 segundos más y reintentando refresh...",
                  );
                  await new Promise((resolve) => setTimeout(resolve, 2000));
                  const retryResponse =
                    await subscriptionService.refreshSubscription();
                  if (
                    retryResponse &&
                    retryResponse.success &&
                    retryResponse.data
                  ) {
                    console.log(
                      "[SUBSCRIPTION] subscription_status después de retry:",
                      retryResponse.data.status,
                    );
                    if (
                      retryResponse.data.status === "active" ||
                      retryResponse.data.status === "trialing"
                    ) {
                      paymentConfirmed = true;
                    }
                  }
                }
              }
            } catch (refreshError) {
              console.error(
                "[SUBSCRIPTION] ❌ Error al actualizar suscripción:",
                refreshError,
              );
            }
          } else if (paymentIntent.status === "requires_action") {
            console.log(
              "[SUBSCRIPTION] ✅ PaymentIntent requires_action - 3D Secure completado, esperando procesamiento...",
            );

            await new Promise((resolve) => setTimeout(resolve, 3000));

            try {
              console.log(
                "[SUBSCRIPTION] Llamando a /api/subscription/refresh después de 3D Secure...",
              );
              const refreshResponse =
                await subscriptionService.refreshSubscription();
              if (
                refreshResponse &&
                refreshResponse.success &&
                refreshResponse.data
              ) {
                const status = refreshResponse.data.status;
                const piStatus = refreshResponse.data.payment_intent_status;
                console.log("[SUBSCRIPTION] Estado después de 3D Secure:", {
                  subscription_status: status,
                  payment_intent_status: piStatus,
                });

                if (
                  status === "active" ||
                  status === "trialing" ||
                  piStatus === "succeeded"
                ) {
                  paymentConfirmed = true;
                  console.log(
                    "[SUBSCRIPTION] ✅ Suscripción activada después de 3D Secure",
                  );
                } else {
                  console.log(
                    "[SUBSCRIPTION] Esperando procesamiento adicional (2 segundos)...",
                  );
                  await new Promise((resolve) => setTimeout(resolve, 2000));
                  const retryResponse =
                    await subscriptionService.refreshSubscription();
                  if (
                    retryResponse &&
                    retryResponse.success &&
                    retryResponse.data
                  ) {
                    console.log(
                      "[SUBSCRIPTION] Estado después de retry:",
                      retryResponse.data,
                    );
                    if (
                      retryResponse.data.status === "active" ||
                      retryResponse.data.status === "trialing"
                    ) {
                      paymentConfirmed = true;
                    }
                  }
                }
              }
            } catch (refreshError) {
              console.warn(
                "[SUBSCRIPTION] ⚠️ Error al actualizar suscripción después de 3D Secure:",
                refreshError.message,
              );
            }
          } else {
            console.warn(
              "[SUBSCRIPTION] ⚠️ PaymentIntent en estado inesperado:",
              paymentIntent.status,
            );
            console.warn(
              "[SUBSCRIPTION] Estados esperados: succeeded, requires_action",
            );
            console.warn("[SUBSCRIPTION] Estado actual:", paymentIntent.status);

            await new Promise((resolve) => setTimeout(resolve, 3000));
            try {
              console.log(
                "[SUBSCRIPTION] Llamando a /api/subscription/refresh para verificar estado...",
              );
              const refreshResponse =
                await subscriptionService.refreshSubscription();
              console.log(
                "[SUBSCRIPTION] Estado después de espera:",
                refreshResponse?.data,
              );
            } catch (refreshError) {
              console.warn(
                "[SUBSCRIPTION] ⚠️ Error al actualizar suscripción:",
                refreshError.message,
              );
            }
          }
        } catch (confirmErr) {
          console.error(
            "[SUBSCRIPTION] ========================================",
          );
          console.error("[SUBSCRIPTION] ❌ EXCEPCIÓN al confirmar pago");
          console.error("[SUBSCRIPTION] Error:", confirmErr.message);
          console.error("[SUBSCRIPTION] Stack:", confirmErr.stack);
          console.error(
            "[SUBSCRIPTION] ========================================",
          );
          throw confirmErr;
        }
      } else if (requires_payment_confirmation === false) {
        console.log("[SUBSCRIPTION] Stripe no requiere confirmación manual");
        console.log(
          "[SUBSCRIPTION] Llamando a /api/subscription/refresh para sincronizar estado...",
        );

        try {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const refreshResponse =
            await subscriptionService.refreshSubscription();
          console.log(
            "[SUBSCRIPTION] Respuesta /subscription/refresh:",
            refreshResponse?.data,
          );

          if (
            refreshResponse &&
            refreshResponse.success &&
            refreshResponse.data
          ) {
            const status = refreshResponse.data.status;
            if (status === "active" || status === "trialing") {
              paymentConfirmed = true;
              console.log(
                "[SUBSCRIPTION] ✅ Suscripción activa (sin confirmación manual requerida)",
              );
            }
          }
        } catch (refreshError) {
          console.warn(
            "[SUBSCRIPTION] ⚠️ Error al actualizar suscripción:",
            refreshError.message,
          );
        }
      } else {
        console.warn("[SUBSCRIPTION] ========================================");
        console.warn("[SUBSCRIPTION] ⚠️ NO se llamó a confirmCardPayment()");
        console.warn("[SUBSCRIPTION] Razón:");
        console.warn("[SUBSCRIPTION]   - has_client_secret:", !!client_secret);
        console.warn(
          "[SUBSCRIPTION]   - requires_payment_confirmation:",
          requires_payment_confirmation,
        );
        console.warn("[SUBSCRIPTION] ========================================");

        await new Promise((resolve) => setTimeout(resolve, 3000));
        try {
          console.log(
            "[SUBSCRIPTION] Llamando a /api/subscription/refresh para verificar estado...",
          );
          const refreshResponse =
            await subscriptionService.refreshSubscription();
          console.log(
            "[SUBSCRIPTION] Respuesta /subscription/refresh:",
            refreshResponse?.data,
          );

          if (
            refreshResponse &&
            refreshResponse.success &&
            refreshResponse.data
          ) {
            const status = refreshResponse.data.status;
            console.log(
              "[SUBSCRIPTION] Estado después de verificación:",
              status,
            );
            if (status === "active" || status === "trialing") {
              paymentConfirmed = true;
              console.log(
                "[SUBSCRIPTION] ✅ Suscripción activada automáticamente",
              );
            } else {
              console.warn(
                "[SUBSCRIPTION] ⚠️ Suscripción sigue en estado:",
                status,
              );
            }
          }
        } catch (refreshError) {
          console.warn(
            "[SUBSCRIPTION] ⚠️ Error al verificar suscripción:",
            refreshError.message,
          );
        }
      }

      console.log("[SUBSCRIPTION] ========================================");
      console.log(
        "[SUBSCRIPTION] Iniciando refresh de usuario y suscripción...",
      );
      console.log("[SUBSCRIPTION] ========================================");

      const subscriptionActive = await refreshUserAndSubscription();

      if (subscriptionActive || paymentConfirmed) {
        /* El código del closer ya cumplió: se cobró con él. Dejarlo guardado
           haría que reapareciera aplicado en una compra futura de la misma
           persona, atribuyendo al closer una venta que no hizo. */
        limpiarCodigo();

        toast({
          title: "¡Suscripción activa!",
          description: "Tu suscripción está activa, disfruta tus beneficios 😉",
        });

        console.log("[SUBSCRIPTION] Redirigiendo a /home...");
        setTimeout(() => {
          navigate("/home", { replace: true });
        }, 1500);
      } else {
        console.warn(
          "[SUBSCRIPTION] ⚠️ El pago puede estar pendiente. La suscripción se activará cuando se procese.",
        );
        toast({
          title: "Pago en proceso",
          description:
            "Tu pago está siendo procesado. La suscripción se activará en breve.",
          variant: "default",
        });

        setTimeout(() => {
          navigate("/home", { replace: true });
        }, 3000);
      }
    } catch (err) {
      console.error("❌ Error en pago:", err);

      let errorMessage = "Error al procesar el pago. Intenta nuevamente.";
      if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    price,
    loadPrice,
    plans,
    loadPlans,
    selectedPlan,
    setSelectedPlan,
    couponCode,
    setCouponCode,
    handlePayment,
  };
}
